/**
 * The guided demo is a look, not a workspace (#2824). A visitor runs the
 * scenario: Solve, Publish roster, decide requests, Change the scenario.
 * Every other edit is locked. The API refuses it (`auth.demo_session_gate`);
 * this hook lets a control hide or go read-only first.
 */
import { isGuidedDemo } from "./demoSession";

/** True in a demo session: hide or disable controls that edit the team. */
export function useDemoLocked(): boolean {
  // Per tab (#2824): only a tab that mounted a demo auth provider locks, so a
  // signed-in lead's tab stays open while another tab runs a demo.
  return isGuidedDemo();
}

/** The dates a demo covers: its stored roster's window (inclusive ISO dates). */
export interface DemoDates {
  from: string;
  to: string;
}

/**
 * Keep a window of dates inside the demo's dates. A window wholly outside
 * becomes the demo's dates. With no demo dates the window is returned as is.
 */
export function clampToDemoDates<T extends { from: string; to: string }>(
  window: T,
  demo: DemoDates | null,
): T {
  if (!demo) return window;
  const from = window.from < demo.from ? demo.from : window.from;
  const to = window.to > demo.to ? demo.to : window.to;
  if (from > to) return { ...window, from: demo.from, to: demo.to };
  if (from === window.from && to === window.to) return window;
  return { ...window, from, to };
}

export function isDemoDates(value: DemoDates | null | undefined): value is DemoDates {
  return Boolean(value?.from && value?.to);
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function dayMonth(iso: string): string {
  return `${Number(iso.slice(8, 10))} ${MONTH_NAMES[Number(iso.slice(5, 7)) - 1] ?? ""}`;
}

/** The short line that names the demo's dates where a Solve is held back. */
export function demoCoversLine(demo: DemoDates): string {
  return `The demo covers ${dayMonth(demo.from)} to ${dayMonth(demo.to)}.`;
}

/** True when any date of the window lies outside the demo's dates. */
export function reachesOutsideDemo(window: { from: string; to: string }, demo: DemoDates): boolean {
  return window.from < demo.from || window.to > demo.to;
}
